import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Button,
  Grid,
  Dialog,
  DialogActions,
  IconButton,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { tokens } from "../../../theme";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useDispatch, useSelector } from "react-redux";
import {
  listRestaurantInvoiceDetails,
  unlinkRestaurantInvoice,
  updateRestaurantInvoiceStatus,
} from "../../../store/actions";
import { PDFViewer } from "@react-pdf/renderer";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";

import InvoicePDF from "./InvoicePDF";
import LinkBookingInvoiceButton from "./LinkBookingInvoiceButton";
import { toast } from "react-toastify";

const RestaurantBillDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const dispatch = useDispatch();

  const { restaurantInvoicesDetails, hotelProfile, isLoading } = useSelector(
    (state) => ({
      restaurantInvoicesDetails: state.admin.restaurantInvoicesDetails,
      hotelProfile: state.registration.hotelProfile,
      isLoading: state.loading,
    })
  );

  const gstEnabled = Boolean(hotelProfile?.gst_enabled);

  const [bill, setBill] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const pdfData = useMemo(() => {
    // Use the restaurant's own billing identity when it's a separate GST entity
    // and that field is configured; otherwise fall back to the hotel's data.
    const rest = hotelProfile?.restaurant || {};
    const useRestaurant = Boolean(hotelProfile?.has_separate_restaurant);
    const pick = (restValue, hotelValue) =>
      (useRestaurant && restValue) || hotelValue || "";

    return {
      restaurantInvoicesDetails,
      hotelDetails: {
        name: pick(rest.name, hotelProfile?.name),
        address: pick(rest.address, hotelProfile?.address),
        phone: pick(rest.phone, hotelProfile?.phone),
        mobile: hotelProfile?.mobile || "",
        email: pick(rest.email, hotelProfile?.email),
        gstin: pick(rest.gstin, hotelProfile?.gstin),
        logo: hotelProfile?.logo || "",
        bank_details: hotelProfile?.bank_details || {},
        cin: hotelProfile?.cin || "",
        pan: hotelProfile?.pan || "",
      },
      gstEnabled,
    };
  }, [restaurantInvoicesDetails, hotelProfile, gstEnabled]);

  useEffect(() => {
    if (id) dispatch(listRestaurantInvoiceDetails({ id }));
  }, [id, dispatch]);

  useEffect(() => {
    if (restaurantInvoicesDetails) setBill(restaurantInvoicesDetails);
  }, [restaurantInvoicesDetails]);

  if (isLoading || !bill) {
    return (
      <Box
        sx={{
          height: "70vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  const formattedDate = new Date(bill.inv_date * 1000).toLocaleString();

  // GST rates are server-authoritative — derive the displayed % from the amounts
  // so linked bills (which inherit the booking's room slab) render correctly.
  const fmtPct = (v) => Number((v || 0).toFixed(2));
  const sgstPct = bill?.amount ? fmtPct((bill.sgst / bill.amount) * 100) : 0;
  const cgstPct = bill?.amount ? fmtPct((bill.cgst / bill.amount) * 100) : 0;
  const hasGst = (bill?.sgst || 0) > 0 || (bill?.cgst || 0) > 0;

  const handleMarkAsPaid = async () => {
    await dispatch(
      updateRestaurantInvoiceStatus({ id: bill._id, status: "paid" })
    );
    dispatch(listRestaurantInvoiceDetails({ id }));
  };

  const handleUpdatePaid = async () => {
    await dispatch(
      updateRestaurantInvoiceStatus({ id: bill._id, status: "unpaid" })
    );
    dispatch(listRestaurantInvoiceDetails({ id }));
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, minHeight: "100vh" }}>
      <Paper
        elevation={4}
        sx={{
          p: { xs: 2, sm: 4 },
          maxWidth: 1200,
          margin: "0 auto",
          borderRadius: 2,
          bgcolor: colors.primary[400],
        }}
      >
        <Stack spacing={3}>
          {/* Header */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              borderBottom: "1px solid",
              borderColor: colors.grey[700],
              pb: 1,
            }}
          >
            <Typography variant="h5" fontWeight={600}>
              Restaurant Invoice
            </Typography>

            <Stack direction="row" spacing={1}>
              {!bill?.booking_id?.trim() ? (
                <LinkBookingInvoiceButton colors={colors} />
              ) : (
                <LinkedBookingActions
                  bill={bill}
                  colors={colors}
                  navigate={navigate}
                />
              )}

              <Button
                variant="outlined"
                color="secondary"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(-1)}
              >
                Back
              </Button>

              <Button
                variant="contained"
                startIcon={<VisibilityIcon />}
                onClick={() => setShowPreview(true)}
              >
                Preview PDF
              </Button>
            </Stack>
          </Stack>

          {/* Invoice Info */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2">Invoice No</Typography>
              <Typography fontWeight={600}>{bill?.inv_no}</Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2">Date & Time</Typography>
              <Typography fontWeight={600}>{formattedDate}</Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2">Customer Name</Typography>
              <Typography fontWeight={600}>{bill?.customer_name}</Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2">Mobile</Typography>
              <Typography fontWeight={600}>
                {bill?.customer_mobile || "—"}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2">Payment Status</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  label={bill?.pmt_status?.replace("_", " ").toUpperCase()}
                  color={
                    bill?.pmt_status === "paid"
                      ? "success"
                      : bill?.pmt_status === "credit"
                      ? "info"
                      : bill?.pmt_status === "partially_paid"
                      ? "warning"
                      : "error"
                  }
                  size="small"
                />
                {bill?.pmt_status !== "paid" ? (
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    onClick={handleMarkAsPaid}
                  >
                    Mark as Paid
                  </Button>
                ) : (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleUpdatePaid}
                  >
                    Set as Unpaid
                  </Button>
                )}
              </Stack>
            </Grid>
          </Grid>

          <Divider />

          {/* Items */}
          <Box>
            <Typography variant="h6" mb={1}>
              Ordered Items
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: colors.blueAccent[700] }}>
                  <TableCell>Item</TableCell>
                  <TableCell align="center">Qty</TableCell>
                  <TableCell align="right">Rate (₹)</TableCell>
                  <TableCell align="right">Amount (₹)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bill?.items?.map((item, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{item.item_name}</TableCell>
                    <TableCell align="center">{item.quantity}</TableCell>
                    <TableCell align="right">{item.rate?.toFixed(2)}</TableCell>
                    <TableCell align="right">
                      {(item.amount || item.rate * item.quantity).toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          {/* Totals */}
          <Box textAlign="right">
            <Typography>Subtotal: ₹{bill?.amount?.toFixed(2)}</Typography>

            {gstEnabled && hasGst && (
              <>
                <Typography>
                  SGST @ {sgstPct}%: ₹{bill?.sgst?.toFixed(2)}
                </Typography>
                <Typography>
                  CGST @ {cgstPct}%: ₹{bill?.cgst?.toFixed(2)}
                </Typography>
              </>
            )}

            <Typography variant="h6" fontWeight={700} color="secondary">
              Grand Total: ₹{bill?.total?.toFixed(2)}
            </Typography>
          </Box>

          {/* Remarks */}
          {bill?.remarks?.trim() && (
            <>
              <Divider />
              <Typography>
                <b>Remarks:</b> {bill.remarks}
              </Typography>
            </>
          )}
        </Stack>
      </Paper>

      {/* PDF Preview */}
      <Dialog
        maxWidth="lg"
        fullWidth
        open={showPreview}
        onClose={() => setShowPreview(false)}
      >
        <DialogActions sx={{ justifyContent: "space-between" }}>
          <Typography variant="h6">Invoice Preview</Typography>
          <IconButton onClick={() => setShowPreview(false)}>
            <CloseIcon />
          </IconButton>
        </DialogActions>
        <DialogContent sx={{ p: 0, height: "100vh" }}>
          <PDFViewer width="100%" height="100%">
            <InvoicePDF data={pdfData} />
          </PDFViewer>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default RestaurantBillDetailPage;

const LinkedBookingActions = ({ bill, colors, navigate }) => {
  const [openUnlink, setOpenUnlink] = useState(false);
  const dispatch = useDispatch();

  // 🔹 Dummy function (Replace with API call later)
const handleUnlinkConfirm = async () => {
  const result = await dispatch(unlinkRestaurantInvoice({ restaurantInvoiceId: bill._id }));
  
  if (result.success) {
      toast.success(result.message);
      // Refresh the bill details to update the UI
      dispatch(listRestaurantInvoiceDetails({ id: bill._id })); 
      setOpenUnlink(false);
  } else {
      toast.error(result.message);
  }
};

  return (
    <>
      <Stack direction="row" spacing={1} alignItems="center">
        {/* Existing: Go to Booking */}
        {/* <Button
          variant="outlined"
          onClick={() => navigate(`/billing/bookings/${bill.booking_id}`)}
          sx={{
            borderColor: colors?.primary?.[100],
            color: colors?.grey?.[100],
            "&:hover": { borderColor: colors?.primary?.[300] },
          }}
        >
          View Booking
        </Button> */}

        {/* New: Unlink Button */}
        <Button
          variant="outlined"
          color="error"
          onClick={() => setOpenUnlink(true)}
        >
          Unlink
        </Button>
      </Stack>

      {/* Unlink Confirmation Dialog */}
      <Dialog open={openUnlink} onClose={() => setOpenUnlink(false)}>
        <DialogTitle>Confirm Unlink</DialogTitle>
        <DialogContent dividers>
          <Typography>
            Are you sure you want to unlink this bill from the hotel booking?
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: "block" }}
          >
            * This will remove the charge from the room folio.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenUnlink(false)}>Cancel</Button>
          <Button
            onClick={handleUnlinkConfirm}
            variant="contained"
            color="error"
          >
            Yes, Unlink
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
